import React, { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { compareAsc, format, isSameDay, parseISO } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { Button, Spinner, Typography, useDimensions, Icon } from '@telavita-core/react-design-kit'

import HeaderMenu from 'containers/HeaderMenu'
import Footer from 'components/Footer'
import { RegisterCompleteFeedback } from './components'

import * as routes from 'settings/_routesSettings'
import { history } from 'routers/AppRouter'
import * as actions from 'redux/actions'

import { ReactComponent as CheckIcon} from 'static/svg/icon-check-green.svg'
import { Specialities } from 'settings/_specialitiesSettings'
import { configMixpanelCookieExpiration, mixpanelInstance } from 'settings/mixpanel'
import { FROM_HOMEPAGE, FROM_PROFESSIONAL_PROFILE, FROM_NPS } from 'settings/_scheduledFrom'
import { usePersonGeneralData } from 'hooks'
import { invalidatePatientReservationCardCache } from 'utils/invalidatePatientReservationCardCache'

const PlanResponse = () => {
  const { isMobile } = useDimensions()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const location = useLocation()
  const order = useSelector(state => state.orders.order)
  const profileSelected = useSelector(state => state.auth.profileSelected)
  const orderId = useSelector(state => state.orders.orderId)
  const isLoading = useSelector(state => state.orders.isLoading)
  const professionalName = useSelector(state => state.orders.professionalName)
  const { hasAddressData, hasRelativeData } = usePersonGeneralData()

  const sortedSchedules = order.schedules && order.schedules.sort((a, b) => compareAsc(a.start_date, b.start_date))

  const allowSchedulingBecomingReservation = Boolean(order?.autoReservationEnabled)

  const reservationNonRecurrentExceptions = location.state?.reservationNonRecurrentExceptions

  const showReservationSuccessMessaging =
    allowSchedulingBecomingReservation &&
    location.state?.reservationConfirmationSucceeded === true

  const planMatchingOrder = profileSelected?.plans?.find(
    (planItem) => planItem.partner_plan_code === order?.plan_code
  )
  const allowsAutomaticSchedulingViaReservation = Boolean(
    planMatchingOrder?.allows_automatic_scheduling_via_reservation
  )

  const professionalRole = order && Specialities.find(speciality => speciality.code === order?.profile_role)?.role
  const isPsychiatrist = order && order?.profile_role === 'PSIQ'

  const shouldShowRegisterCompleteFeedback = isPsychiatrist && (!hasAddressData || !hasRelativeData)

  useEffect(() => {
    if (!orderId) return history.push(routes.DASHBOARD_APPOINTMENTS)
    dispatch(actions.getOrders(orderId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[order])

  /*
    Essa configuração do cookie do Mixpanel é necessária para que o time de marketing tenha
    mais precisão na análise dos dados quando o paciente agenda uma consulta.
  */
  useEffect(() => {
    if (orderId) {
      const EXPIRATION_IN_DAYS = 1

      configMixpanelCookieExpiration(EXPIRATION_IN_DAYS)
    }

  }, [orderId])

  useEffect(() => {
    if (orderId) {
      const eventMapper = {
        [FROM_HOMEPAGE]: 'Agendamento pela vitrine',
        [FROM_PROFESSIONAL_PROFILE]: 'Agendamento pelo perfil do profissional',
        [FROM_NPS]: 'Agendamento pela página de NPS'
      }

      const eventName = eventMapper[location.state?.scheduledFrom] || ''
      mixpanelInstance.track(eventName, {
        'Plano': order?.plan_code,
        'Data(s) agendadas(s)': sortedSchedules?.map(schedule =>
          `${format(new Date(schedule.start_date), 'dd/MM/yyyy')} às ${format(new Date(schedule.start_date), 'HH:mm')}`
        ),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  useEffect(() => {
    window.scrollTo(0, 0)
  },[])

  useEffect(() => {
    if (location.state?.reservationConfirmationSucceeded === true) {
      invalidatePatientReservationCardCache(queryClient)
    }
  }, [location.state?.reservationConfirmationSucceeded, queryClient])

  return (
    <div className='PlanResponse'>
      <HeaderMenu />
      <div className='PlanResponse__AppointmentContainer'>
        {orderId && isLoading ? (
          <Spinner />
        ) : (
          <>
            {shouldShowRegisterCompleteFeedback && (
              <RegisterCompleteFeedback />
            )}
            <CheckIcon />
            <Typography variant='header4' weight='bold'>
              Consulta agendada com sucesso!
            </Typography>
            <div className='PlanResponse__AppointmentInfo'>
              <Typography variant='content2'>
                Com o {professionalRole}
              </Typography>
              <Typography variant='content1' weight='bold'>
                {professionalName}
              </Typography>
              {sortedSchedules && sortedSchedules.map(schedule => (
                <Typography variant='content1' weight='bold'>
                  {format(new Date(schedule.start_date), 'dd/MM/yyyy')}, às {format(new Date(schedule.start_date), 'HH\'h\'mm')}
                </Typography>
              ))}
              {showReservationSuccessMessaging && (
                <div className='PlanResponse__AppointmentSchedulingBecomingReservation'>
                  <div className='PlanResponse__AppointmentSchedulingBecomingReservation__title'>
                    <Icon name='RefreshClockSuccess' fill='#4AC326' width={32} />
                    <Typography weight='bolder'>
                      Também reservamos este dia e horário para você nas próximas semanas!
                    </Typography>
                  </div>

                  {reservationNonRecurrentExceptions?.length > 0 && (
                  <div className='PlanResponse__AppointmentSchedulingBecomingReservation__exceptions'>
                    <Typography weight='bolder' variant='content2'>
                      Exceto em:
                    </Typography>
                    {reservationNonRecurrentExceptions.map((nonRecurrentException) => {
                      const restrictedWeekdayDate = parseISO(nonRecurrentException.exceptionDateIso)
                      const replacementSlotStart = parseISO(nonRecurrentException.replacementStartIso)
                      const restrictedDateLabel = format(restrictedWeekdayDate, 'dd/MM/yyyy')
                      const replacementDateLabel = format(replacementSlotStart, 'dd/MM/yyyy')
                      const replacementTimeLabel = format(replacementSlotStart, 'HH:mm')
                      const isReplacementOnSameCalendarDay = isSameDay(
                        restrictedWeekdayDate,
                        replacementSlotStart
                      )
                      const rowKey = `${nonRecurrentException.exceptionDateIso}-${nonRecurrentException.replacementStartIso}`

                      return isReplacementOnSameCalendarDay
                        ? (
                          <Typography key={rowKey} variant='content2'>
                            <b>{restrictedDateLabel},</b> reservado para <b>{replacementTimeLabel}</b>
                          </Typography>
                        )
                        : (
                          <Typography key={rowKey} variant='content2'>
                            <b>{restrictedDateLabel},</b> reservado para <b>{replacementDateLabel}, às {replacementTimeLabel}</b>
                          </Typography>
                        )
                    })}
                  </div>
                  )}

                  <div className='PlanResponse__AppointmentSchedulingBecomingReservation__info'>
                    <Typography variant='content2'>
                      {allowsAutomaticSchedulingViaReservation
                        ? (
                          <>
                            <b>Assim, você não precisa agendar as próximas consultas manualmente.</b> As reservas são convertidas automaticamente em consultas agendadas, desde que você possua créditos.
                          </>
                        )
                        : (
                          <>
                            <b>Continue agendando normalmente.</b> A diferença é que, a partir de agora, estes dias e horários vão aparecer somente pra você ao agendar.
                          </>
                        )}
                    </Typography>
                  </div>
                </div>
              )}
            </div>
            {showReservationSuccessMessaging
              ? (
                <Typography variant='content2' center>
                  Você receberá estes detalhes por e-mail e um lembrete próximo à data da consulta, para garantir que você não se esqueça.
                </Typography>
              ) : (
                <Typography variant='content2' center>
                  Você receberá um e-mail de confirmação com os detalhes.
                  Também enviaremos um SMS perto da data agendada, para garantir que você não perca a consulta.
                </Typography>
              )
            }
            
            <Button
              variant='contained'
              customClassName='PlanResponse__Button'
              uppercase={false}
              onClick={() => history.push(routes.DASHBOARD_HOME_PATIENT)}
            >
              Ir para a página inicial
            </Button>
          </>
        )}
      </div>
      <Footer
        isMobile={isMobile}
        extraClass='PlanResponse__Footer'
      />
    </div>
  )
}

export default PlanResponse
